import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Wrench, Send, CheckCircle, MapPin, AlertTriangle, Camera, Phone, Loader2, LogIn, User, Building2, ImagePlus, X, Trash2, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicReportPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketCode, setTicketCode] = useState('');
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    description: '',
    reporter_name: '',
    reporter_phone: '',
    reporter_department: '',
    priority_requested: '',
    images: []  // {id, preview, base64}
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) setIsLoggedIn(true);
    fetchEquipment();
  }, [code]);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/public/equipment/${code}`);
      setEquipment(response.data);
      // Auto-fill department from equipment
      if (response.data.departament) {
        setFormData(prev => ({ ...prev, reporter_department: response.data.departament }));
      }
      // Auto-fill priority from equipment's ticket_priority
      if (response.data.ticket_priority) {
        setFormData(prev => ({ ...prev, priority_requested: response.data.ticket_priority }));
      } else {
        setFormData(prev => ({ ...prev, priority_requested: 'medium' }));
      }
      if (!response.data.public_reporting_enabled) {
        setError('Raportarea publica este dezactivata pentru acest sistem.');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Echipamentul nu a fost gasit. Verificati codul QR.');
      } else {
        setError('Eroare la incarcarea datelor. Incercati din nou.');
      }
    } finally {
      setLoading(false);
    }
  };

  const goToFullView = () => navigate(`/equipment/${code}`);

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (formData.images.length + files.length > 5) {
      toast.error('Maximum 5 fotografii permise');
      return;
    }
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: Fisierul depaseste 5MB`);
        continue;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64 = ev.target.result;
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, {
            id: Date.now() + Math.random(),
            preview: base64,
            base64: base64.split(',')[1] // strip data:image/...;base64, prefix for storage
          }]
        }));
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const removeImage = (id) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter(img => img.id !== id) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) { toast.error('Descrieti problema observata'); return; }
    if (!formData.reporter_name.trim()) { toast.error('Numele este obligatoriu'); return; }
    if (!formData.reporter_phone.trim()) { toast.error('Telefonul este obligatoriu'); return; }
    
    try {
      setSubmitting(true);
      const response = await axios.post(`${API_URL}/api/public/tickets`, {
        equipment_code: code,
        description: formData.description.trim(),
        reporter_name: formData.reporter_name.trim(),
        reporter_phone: formData.reporter_phone.trim(),
        reporter_department: formData.reporter_department.trim() || null,
        priority_requested: formData.priority_requested || 'medium',
        images: formData.images.map(img => img.base64)
      });
      setTicketCode(response.data.ticket_code);
      setSubmitted(true);
      toast.success('Problema a fost raportata!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la trimitere. Incercati din nou.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
        <p className="text-slate-300">Se incarca...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800 border-slate-700">
        <CardContent className="p-6 text-center">
          <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Eroare</h2>
          <p className="text-slate-400">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800 border-slate-700">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Trimis cu succes!</h2>
          <p className="text-slate-400 mb-6">Problema a fost raportata si echipa de mentenanta a fost notificata.</p>
          <div className="bg-slate-700/50 rounded-xl p-4 mb-6">
            <p className="text-slate-400 text-sm mb-1">Cod tichet:</p>
            <p className="text-2xl font-mono font-bold text-blue-400" data-testid="ticket-code">{ticketCode}</p>
          </div>
          <p className="text-slate-500 text-sm mb-4">Pastrati acest cod pentru a urmari statusul raportarii.</p>
          <p className="text-slate-400 text-sm">Veti primi o notificare cand problema va fi rezolvata.</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 p-4">
      <div className="max-w-md mx-auto pt-6 pb-8">
        
        {isLoggedIn && (
          <div className="mb-4 p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
            <div className="flex items-center justify-between">
              <p className="text-blue-300 text-sm">Esti logat in sistem</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-indigo-400 text-indigo-400 hover:bg-indigo-500/20" onClick={() => navigate(`/knowledge-base?equipment=${equipment?.id}`)}>
                  <BookOpen className="w-4 h-4 mr-1" /> Documente
                </Button>
                <Button size="sm" variant="outline" className="border-blue-400 text-blue-400 hover:bg-blue-500/20" onClick={goToFullView}>
                  <LogIn className="w-4 h-4 mr-1" /> Acces Complet
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Equipment Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Wrench className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-1" data-testid="equipment-name">{equipment?.name}</h1>
          <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
            <MapPin className="w-4 h-4" />
            <span>{equipment?.location_name || equipment?.plant_name || 'Locatie necunoscuta'}</span>
          </div>
          {equipment?.code && (
            <p className="text-slate-500 text-xs mt-1">Cod: {equipment.code}</p>
          )}
          {equipment?.marca && (
            <p className="text-slate-500 text-xs mt-0.5">{equipment.marca} {equipment.serie_utilaj ? `| ${equipment.serie_utilaj}` : ''}</p>
          )}
        </div>
        
        {/* Report Form */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-5">
            <form onSubmit={handleSubmit} className="space-y-4">
              
              {/* Problem Description */}
              <div className="space-y-2">
                <Label className="text-white text-base flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Ce problema ai observat? *
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrie problema pe scurt..."
                  rows={3}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 text-base resize-none"
                  autoFocus
                  data-testid="problem-description"
                />
              </div>

              {/* Photo Upload */}
              <div className="space-y-2">
                <Label className="text-white text-sm flex items-center gap-2">
                  <Camera className="w-4 h-4 text-blue-400" />
                  Fotografii (optional, max 5)
                </Label>
                
                {formData.images.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {formData.images.map(img => (
                      <div key={img.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-600">
                        <img src={img.preview} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removeImage(img.id)}
                          className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center">
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {formData.images.length < 5 && (
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm"
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700 h-10"
                      onClick={() => cameraInputRef.current?.click()}
                      data-testid="btn-camera"
                    >
                      <Camera className="w-4 h-4 mr-1.5" /> Fotografiaza
                    </Button>
                    <Button type="button" variant="outline" size="sm"
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700 h-10"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="btn-upload-photo"
                    >
                      <ImagePlus className="w-4 h-4 mr-1.5" /> Incarca
                    </Button>
                    <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleImageSelect} />
                    <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageSelect} />
                  </div>
                )}
              </div>

              {/* Required fields separator */}
              <div className="border-t border-slate-700 pt-4 space-y-3">
                <p className="text-slate-400 text-xs font-medium uppercase tracking-wider">Date obligatorii:</p>
                
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <User className="w-4 h-4" /> Numele tau *
                  </Label>
                  <Input
                    value={formData.reporter_name}
                    onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value })}
                    placeholder="ex: Ion Popescu"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 h-11"
                    required
                    data-testid="reporter-name"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Telefon *
                  </Label>
                  <Input
                    type="tel"
                    value={formData.reporter_phone}
                    onChange={(e) => setFormData({ ...formData, reporter_phone: e.target.value })}
                    placeholder="ex: 0722 123 456"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 h-11"
                    required
                    data-testid="reporter-phone"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Departament / Sectie
                  </Label>
                  <Input
                    value={formData.reporter_department}
                    onChange={(e) => setFormData({ ...formData, reporter_department: e.target.value })}
                    placeholder="ex: Hala Productie"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 h-11"
                    data-testid="reporter-department"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Prioritate
                  </Label>
                  <select
                    value={formData.priority_requested}
                    onChange={(e) => setFormData({ ...formData, priority_requested: e.target.value })}
                    className="w-full h-11 rounded-md border border-slate-600 bg-slate-700 text-white px-3 text-sm"
                    data-testid="priority-select"
                  >
                    <option value="critical">Critic</option>
                    <option value="urgent">Urgent</option>
                    <option value="high">Ridicat</option>
                    <option value="medium">Mediu</option>
                    <option value="low">Scazut</option>
                  </select>
                </div>
              </div>
              
              {/* Submit Button */}
              <Button
                type="submit"
                disabled={submitting || !formData.description.trim() || !formData.reporter_name.trim() || !formData.reporter_phone.trim()}
                className="w-full h-14 text-lg bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50"
                data-testid="submit-report"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Se trimite...</>
                ) : (
                  <><Send className="w-5 h-5 mr-2" /> RAPORTEAZA PROBLEMA</>
                )}
              </Button>
              
            </form>
          </CardContent>
        </Card>
        
        <p className="text-center text-slate-500 text-xs mt-4">
          Echipa de mentenanta va fi notificata automat si va interveni in cel mai scurt timp.
        </p>
      </div>
    </div>
  );
};

export default PublicReportPage;
